import { useMemo } from 'react';

interface CosmicLoaderProps {
    message?: string;
    submessage?: string;
}

const CosmicLoader = ({
    message = "Aligning the Stars",
    submessage = "Preparing your cosmic journey..."
}: CosmicLoaderProps) => {
    // Generate random stars
    const stars = useMemo(() => {
        return Array.from({ length: 40 }, (_, i) => ({
            id: i,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            size: Math.random() * 2 + 1,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${Math.random() * 2 + 2}s`,
            opacity: Math.random() * 0.4 + 0.2,
        }));
    }, []);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-cosmic-950 overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-cosmic-950 via-cosmic-900 to-cosmic-950" />

            {/* Starfield */}
            <div className="absolute inset-0">
                {stars.map((star) => (
                    <div
                        key={star.id}
                        className="absolute rounded-full bg-white animate-twinkle"
                        style={{
                            top: star.top,
                            left: star.left,
                            width: `${star.size}px`,
                            height: `${star.size}px`,
                            opacity: star.opacity,
                            animationDelay: star.animationDelay,
                            animationDuration: star.animationDuration,
                        }}
                    />
                ))}
            </div>

            {/* Atmospheric orbs */}
            <div className="absolute top-[-20%] -left-[10%] w-[60%] h-[50%] bg-purple-500/20 blur-[80px] rounded-full animate-pulse" style={{ animationDuration: '6s' }} />
            <div className="absolute bottom-[-10%] -right-[10%] w-[50%] h-[40%] bg-indigo-500/15 blur-[60px] rounded-full animate-pulse" style={{ animationDuration: '8s' }} />

            {/* Loader content */}
            <div className="relative z-10 flex flex-col items-center gap-8">
                {/* Cosmic rings loader */}
                <div className="relative w-24 h-24">
                    {/* Outer ring */}
                    <div className="absolute inset-0 rounded-full border-2 border-purple-500/20 animate-spin" style={{ animationDuration: '3s' }} />

                    {/* Middle ring */}
                    <div className="absolute inset-2 rounded-full border-2 border-transparent border-t-purple-400/60 border-r-purple-400/30 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />

                    {/* Inner ring */}
                    <div className="absolute inset-4 rounded-full border-2 border-transparent border-t-indigo-400/80 animate-spin" style={{ animationDuration: '1.5s' }} />

                    {/* Center glow */}
                    <div className="absolute inset-4 rounded-full bg-gradient-to-br from-purple-500/40 to-indigo-600/40 blur-sm animate-pulse" />

                    {/* Center BhriguGPT logo */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <img
                            src="/bhrigoo_rishi.webp"
                            alt="BhriguGPT"
                            className="w-12 h-12 object-contain drop-shadow-[0_0_10px_rgba(147,51,234,0.6)] animate-pulse"
                        />
                    </div>
                </div>

                {/* Text content */}
                <div className="text-center space-y-2">
                    <h2 className="text-xl font-serif font-bold text-white tracking-wide">
                        {message}
                    </h2>
                    <p className="text-sm text-purple-300/70 font-sans">
                        {submessage}
                    </p>
                </div>

                {/* Animated dots */}
                <div className="flex gap-1.5">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="w-2 h-2 rounded-full bg-purple-400/80 animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CosmicLoader;
